import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Badge, Col, Divider, Empty, Flex, Row, Select, Skeleton, Space, Tree, Typography } from 'antd';
import { ArrowUpOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import PropTypes from 'prop-types';

// Actions
import { set_selected_node } from '../../../redux/actions/selfActions';

const TreeTitle = (props) => {
	const { node, historyId } = props;
	
	const { t } = useTranslation();

	const { file_info } = useSelector(state => state.self);

	const [badgeValues, setbadgeValues] = useState({ count: t('loadStatusDetails.review.noAction.badge'), color: '#8c8c8c' });
	const [loading, setLoading] = useState(false);
	const [routeLocations, setRouteLocations] = useState({ locations: 0, missing: 0, extra: 0 });

	useEffect(() => {
		if (node?.update === 'C') {
			setbadgeValues({ count: 'C', color: '#06c281' });
		}
		else if (node?.update === 'U') {
			setbadgeValues({ count: t('loadStatusDetails.review.update.badge'), color: '#1890ff' });
		}

		if ('route' in node || 'code' in node) {
			const routeFilters = { 
				organization: file_info?.organization?._id?.$oid,
				workapace: file_info?.workspace?._id?.$oid,
				...(file_info?.load && { load: file_info?.load?._id?.$oid }),
				file: file_info?._id?.$oid,
				...(node?.route ? { route: node?.route } : { code: node?.code }),
				history: historyId
			}
			setLoading(true);
			Axios.get(`${process.env.REACT_APP_SERVER_URL}/api/routes/updates/search?${new URLSearchParams(routeFilters).toString()}`)	
				.then((res) => {
					const routeLocations = {
						locations: res.data.locations.length || 0,
						missing: res.data.missing.length || 0,
						extra: res.data.extra.length || 0
					};
					setRouteLocations(routeLocations);
					setLoading(false);
				}).catch((err) => {
					setRouteLocations({});
					setLoading(false);
				});
		}
	}, [node?.update]);

	return (
		<Space>
			<Badge count={badgeValues.count} color={badgeValues.color} />
			<Typography.Text>{node?.title}</Typography.Text>
			{node?.value &&
				<Fragment>
					{/* <Typography.Text type='secondary'>-&nbsp;&nbsp;{t('routesTable.headers.locations')}: {node?.value}</Typography.Text> */}
					{loading ? <Skeleton.Input active /> : (
						<>
							<Typography.Text type='secondary'>/</Typography.Text>
							<Typography.Text style={{ color: '#1677ff' }}><ArrowUpOutlined /> {parseInt(node?.value) - routeLocations.extra}</Typography.Text>
							{routeLocations.extra > 0 && (
								<>
									<Typography.Text type='secondary'>/</Typography.Text>
									<Typography.Text style={{ color: '#389e0d' }}><PlusOutlined /> {routeLocations.extra}</Typography.Text>
								</>
							)}
							{routeLocations.missing > 0 && (
								<>
									<Typography.Text type='secondary'>/</Typography.Text>
									<Typography.Text type='danger'><MinusOutlined /> {routeLocations.missing}</Typography.Text>
								</>
							)}
						</>
					)}
				</Fragment>
			}
		</Space>
	)
};

TreeTitle.propTypes = {
	node: PropTypes.object.isRequired
}


const LoadFileReview = (props) => {
	const { treeData, historyId, setHistoryId } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { isAdminUser } = useSelector(state => state.auth);

	const { selected_node, file_history } = useSelector(state => state.self);

	const [loadInfo, setLoadInfo] = useState([]);

	const createLoadInfo = (nodes) => {
		if (!Array.isArray(nodes)) return [];

		return nodes.map((node) => {
			const keyNode = `${node.title}-${Math.floor(Math.random() * 1000000)}`;
			let newNode = {
				...node,
				title: <TreeTitle node={node} historyId={historyId} />,
				key: keyNode
			};
	
			if (Array.isArray(node.children)) {
				newNode.children = createLoadInfo(node.children);
			}
	
			return newNode;
		});
	}

	useEffect(() => {
		setHistoryId(file_history.history_latest);
	}, [file_history]);

	useEffect(() => {
		if (historyId !== '') {
			if (Array.isArray(treeData) && treeData.length) {
				setLoadInfo(createLoadInfo(treeData).map(obj => [obj]));
			}
			else {
				if (treeData && typeof treeData === "object" && Object.keys(treeData).length > 0) {
					const newTreeDataArray = [];
					Object.keys(treeData).forEach((key) => {
						newTreeDataArray.push(treeData[key]);
					});
					setLoadInfo(createLoadInfo(newTreeDataArray).map(obj => [obj]));
				}
			}
		}
	}, [treeData, historyId]);

	return (
		<div>
			<Divider />
			<Typography.Title className='text-center mt-4 mb-3' level={4}>{t('loadStatusDetails.review.title')}</Typography.Title>

			<Flex justify='flex-end' style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
				<Space size='large'>
					<Space size={6}>
						<Badge count={t('loadStatusDetails.review.noAction.badge')} showZero color='#8c8c8c'/>
						<Typography.Text type='secondary'> - </Typography.Text>
						<Typography.Text type='secondary'>{t('loadStatusDetails.review.noAction.desc')}</Typography.Text>
					</Space>
					<Space size={6}>
						<Badge count='C' color='#06c281'/>
						<Typography.Text type='secondary'> - </Typography.Text>
						<Typography.Text type='secondary'>{t('loadStatusDetails.review.creation.desc')}</Typography.Text>
					</Space>
					<Space size={6}>
						<Badge count={t('loadStatusDetails.review.update.badge')} color='#1890ff'/>
						<Typography.Text type='secondary'> - </Typography.Text>
						<Typography.Text type='secondary'>{t('loadStatusDetails.review.update.desc')}</Typography.Text>
					</Space>
				</Space>
			</Flex>
			{/* History selector */}
			{isAdminUser &&
				<div>
					<Typography.Text level={4} copyable={{ text: historyId, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
						{t('loadStatusDetails.validationHistory.title')}
					</Typography.Text>
					<Select
						options={file_history.history_options}
						value={historyId}
						placeholder={t('loadStatusDetails.validationHistory.selectHistoryPlaceholder')}
						style={{ width: '100%', marginBottom: '1rem' }}
						onChange={(value) => setHistoryId(value)}
					/>
				</div>
			}
			<Row justify='center' gutter={16} style={{ borderRadius: '5px', backgroundColor: 'white', padding: '10px' }}>
				{loadInfo.length > 0
					? loadInfo.map((node, idx) => (
						<Col
							key={idx}
							style={{ borderRight: idx !== loadInfo.length - 1 ? '1px solid #d9d9d9' : 'none' }}
							xl={8} lg={8} md={8} sm={24} xs={24}
						>
							<Tree
								treeData={node}
								onSelect={(selectedKeys, { node }) => {
									if (Number.isFinite(node.value)) {
										dispatch(set_selected_node(node.key === selected_node.key ? {} : node))
									}
								}}
							/>
						</Col>
					))
					: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
				}
			</Row>
		</div>
	);
}

LoadFileReview.propTypes = {
	treeData: PropTypes.oneOfType([
		PropTypes.array,
		PropTypes.object
	]).isRequired,
	historyId: PropTypes.string.isRequired,
	setHistoryId: PropTypes.func.isRequired
}

export default LoadFileReview;
